import React, { FC, ReactElement, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Col, Input, Row, Table } from 'reactstrap';
import {
  FormNodeVolunteerContactModel,
  VolunteerExternalContactMethod,
} from '../../../../../shared/volunteer/form';
import { MaterialIcon } from '../../../../components';
import { useTranslation } from '../../../../translations';
import { useInputState, useToggle } from '../../../../utils';
import { AbstractElementEditor, UpdateFormProps } from './AbstractElementEditor';

export class VolunteerContactEditor extends AbstractElementEditor<FormNodeVolunteerContactModel> {
  public getDescription(): string {
    return 'Volunteer contact entry';
  }

  public getName(): string {
    return 'Volunteer Contacts';
  }

  public getIcon(): string {
    return 'contacts';
  }

  public override getDefaultValues(): Partial<Omit<FormNodeVolunteerContactModel, 'id' | 'type'>> {
    return {
      externalMethods: [
        {
          name: 'telegram',
          displayName: 'Telegram',
        },
        {
          name: 'discord',
          displayName: 'Discord',
        },
        {
          name: 'bluesky',
          displayName: 'Bluesky',
        },
      ],
    };
  }

  public override getUpdateForm(
    props: UpdateFormProps<FormNodeVolunteerContactModel>,
  ): ReactElement {
    // is there a more elegant way of converting from class to functional?
    return <VolunteerContactUpdateForm {...props} />;
  }
}

const VolunteerContactUpdateForm: FC<UpdateFormProps<FormNodeVolunteerContactModel>> = ({
  node,
  update,
}) => {
  const { ts } = useTranslation();
  const [contactMethods, setContactMethods] = useState(node.externalMethods ?? []);
  const [isCreate, toggleCreate, setIsCreate] = useToggle(false);

  const addContactMethod = useCallback(
    (newMethod: VolunteerExternalContactMethod) => {
      if (
        contactMethods.some((method) => method.name.toLowerCase() === newMethod.name.toLowerCase())
      ) {
        toast.error(ts('that_method_name_already_exists'));
        return;
      }

      const newMethods = [...contactMethods];
      newMethods.push(newMethod);
      setContactMethods(newMethods);
      if (isCreate) {
        setIsCreate(false);
      }
    },
    [contactMethods, isCreate, ts, setIsCreate],
  );

  const deleteContactMethod = useCallback(
    (methodToDelete: string) => {
      if (
        !contactMethods.some((method) => method.name.toLowerCase() === methodToDelete.toLowerCase())
      ) {
        // cover our butts
        toast.error(ts('that_method_does_not_exist_in_the_node_please_refresh'));
        return;
      }

      const newMethods: VolunteerExternalContactMethod[] = contactMethods.filter(
        (method) => method.name.toLowerCase() !== methodToDelete.toLowerCase(),
      );

      setContactMethods(newMethods);
    },
    [contactMethods, ts],
  );

  /*
   * Forward changes up to node
   * I've been using this method in favor of more direct ways with callbacks because state management feels cleaner.
   * Is this correct in terms of doing it the "react way"?
   */
  useEffect(() => {
    update({
      ...node,
      externalMethods: contactMethods.length === 0 ? undefined : contactMethods,
    });
  }, [contactMethods]);

  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h4>{ts('contact_methods')}</h4>
        </Col>
      </Row>
      <Table>
        <tr>
          <th>{ts('name')}</th>
          <th>{ts('display_name_optional')}</th>
          <th style={{ width: '70px', textAlign: 'right' }}>
            <Button
              color={isCreate ? 'danger' : 'success'}
              id={`addOptionValue${isCreate ? 'Cancel' : ''}`}
              onClick={toggleCreate}
              outline={!isCreate}
            >
              <MaterialIcon className="align-middle" name={isCreate ? 'close' : 'add'} />
            </Button>
          </th>
        </tr>
        {isCreate && <NewContactMethod add={addContactMethod} />}
        {contactMethods.map((method) => {
          return (
            <tr key={method.name}>
              <td>{method.name}</td>
              <td>{method.displayName ?? <p className="text-secondary">({ts('none_set')})</p>}</td>
              <td>
                <Button
                  color="danger"
                  id={`deleteContactMethod${method.name}`}
                  onClick={() => {
                    deleteContactMethod(method.name);
                  }}
                  outline
                >
                  <MaterialIcon className="align-middle" name="delete" />
                </Button>
              </td>
            </tr>
          );
        })}
      </Table>
    </>
  );
};

const NewContactMethod: FC<{
  readonly add: (method: VolunteerExternalContactMethod) => void;
}> = ({ add }) => {
  const [name, setName] = useInputState('');
  const [displayName, setDisplayName] = useInputState('');
  const { ts } = useTranslation();
  return (
    <tr id="createNewContactMethod">
      <td>
        <Input maxLength={255} onChange={setName} placeholder="Method Name" type="text" />
      </td>
      <td>
        <Input
          maxLength={255}
          onChange={setDisplayName}
          placeholder={ts('option_editor_select.display_name_optional')}
          type="text"
        />
      </td>
      <td className="text-end">
        <Button
          color="success"
          id="submitNewContactMethod"
          onClick={() => {
            add({ name, displayName });
          }}
          outline
        >
          <MaterialIcon className="align-middle" name="done" />
        </Button>
      </td>
    </tr>
  );
};
